// Output pass for the HDR world: exposure, AgX tone mapping, sRGB encoding.
//
// three r156's OutputPass offers ACES, which oversaturates and hue-shifts
// bright colours (the sunlit crescent went to near-full saturation) and
// clips highlights to white; AgX rolls bright colours off towards white
// instead. This is Filament's AgX (Blender's, with Rec. 2020 primaries), the
// implementation three.js adopted in r160.
export const AgXOutputShader = {
  uniforms: {
    tDiffuse: { value: null },
    toneMappingExposure: { value: 1.0 },
    // Blend towards Blender's "Punchy" look: 0 = base AgX, 1 = full Punchy
    // (too heavy here). 0.4 adds some contrast without the grade looking cooked.
    agxPunchy: { value: 0.4 },
  },
  vertexShader: /* glsl */ `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }`,
  fragmentShader: /* glsl */ `
    uniform sampler2D tDiffuse;
    uniform float toneMappingExposure;
    uniform float agxPunchy;
    varying vec2 vUv;

    const mat3 LINEAR_REC2020_TO_LINEAR_SRGB = mat3(
      vec3(1.6605, -0.1246, -0.0182),
      vec3(-0.5876, 1.1329, -0.1006),
      vec3(-0.0728, -0.0083, 1.1187)
    );
    const mat3 LINEAR_SRGB_TO_LINEAR_REC2020 = mat3(
      vec3(0.6274, 0.0691, 0.0164),
      vec3(0.3293, 0.9195, 0.0880),
      vec3(0.0433, 0.0113, 0.8956)
    );

    // Polynomial fit of AgX's default contrast curve (iolite-engine.com).
    vec3 agxDefaultContrastApprox(vec3 x) {
      vec3 x2 = x * x;
      vec3 x4 = x2 * x2;
      return 15.5 * x4 * x2 - 40.14 * x4 * x + 31.96 * x4 - 6.868 * x2 * x + 0.4298 * x2 + 0.1191 * x - 0.00232;
    }

    // Blender's AgX "Punchy" look: an ASC CDL grade (power 1.35, saturation
    // 1.4) in AgX's encoded space, after the contrast curve.
    vec3 agxPunchyLook(vec3 val) {
      float luma = dot(val, vec3(0.2126, 0.7152, 0.0722));
      val = pow(max(val, 0.0), vec3(1.35));
      return luma + 1.4 * (val - luma);
    }

    vec3 AgXToneMapping(vec3 color) {
      const mat3 AgXInsetMatrix = mat3(
        vec3(0.856627153315983, 0.137318972929847, 0.11189821299995),
        vec3(0.0951212405381588, 0.761241990602591, 0.0767994186031903),
        vec3(0.0482516061458583, 0.101439036467562, 0.811302368396859)
      );
      const mat3 AgXOutsetMatrix = mat3(
        vec3(1.1271005818144368, -0.1413297634984383, -0.14132976349843826),
        vec3(-0.11060664309660323, 1.157823702216272, -0.11060664309660294),
        vec3(-0.016493938717834573, -0.016493938717834257, 1.2519364065950405)
      );
      const float AgxMinEv = -12.47393; // log2(2^-10 * 0.18)
      const float AgxMaxEv = 4.026069;  // log2(2^6.5 * 0.18)
      color = LINEAR_SRGB_TO_LINEAR_REC2020 * color;
      color = AgXInsetMatrix * color;
      color = max(color, 1e-10);
      color = log2(color);
      color = (color - AgxMinEv) / (AgxMaxEv - AgxMinEv);
      color = clamp(color, 0.0, 1.0);
      color = agxDefaultContrastApprox(color);
      color = mix(color, agxPunchyLook(color), agxPunchy);
      color = AgXOutsetMatrix * color;
      color = pow(max(vec3(0.0), color), vec3(2.2));
      color = LINEAR_REC2020_TO_LINEAR_SRGB * color;
      return clamp(color, 0.0, 1.0);
    }

    vec3 linearToSrgb(vec3 c) {
      return mix(c * 12.92, 1.055 * pow(c, vec3(1.0 / 2.4)) - 0.055, step(vec3(0.0031308), c));
    }

    void main() {
      vec4 texel = texture2D(tDiffuse, vUv);
      vec3 color = AgXToneMapping(texel.rgb * toneMappingExposure);
      gl_FragColor = vec4(linearToSrgb(color), 1.0);
    }`,
};
